import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { DateTime as LuxonDateTime } from 'luxon';

import { BookJob } from 'src/providers/book-job/book-job';
import { Card } from 'src/providers/card/card';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Pros } from 'src/providers/pros/pros';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';

import { Loading } from 'src/shared/components/loading/loading';

import { Util } from 'src/shared/util/util';

import { MyProPage } from 'src/pages/my-pros/my-pro/my-pro';

@Component({
    templateUrl: 'send-payment.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class SendPaymentPage implements OnInit, OnDestroy {

  address: any;
  allowsInvoiceAutopay: boolean;
  cameFromGetStarted: boolean;
  creditBalance: number;
  dialogParams: any;
  errorMessage: string;
  form: UntypedFormGroup;
  loaded: boolean;
  jobTimeItems: any = [];
  hasCreditCard: boolean;
  howToPayItems: any[];
  lastFourCC: string;
  isRightSideContent: boolean;
  pro: any;
  privatePlanId: any;
  serviceItems: any;
  serviceCategoryItems: any;
  servicesResponse: any;
  submitted: boolean;
  userRole: string | null;
  paymentPreview: any;
  priceSubscription: Subscription;
  totalOwed = 0;
  paymentAmount = 0;
  boundGoToAddCard = this.goToAddCard.bind(this);
  boundSendPayment = this.sendPayment.bind(this);

constructor(
    private bookJob: BookJob,
    private card: Card,
    private client: Client,
    private fb: UntypedFormBuilder,
    private navCtrl: CustomNavController,
    private pros: Pros,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private util: Util
  ) {
    this.form = this.fb.group({
      service: ['', Validators.required],
      serviceCategory: ['', Validators.required],
      date: ['', Validators.required],
      time: ['', Validators.required],
      price: ['', Validators.required],
      howToPay: ['currentCard']
    });
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.loaded = false;
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
        this.rightSidePanelService.setDialogPageTitle('Send Payment');
      }
      this.pro = this.navCtrl.getParam('pro') || this.dialogParams?.pro || await this.pros.getProDetail(this.navCtrl.getParam('proId') || this.dialogParams?.proId);
      this.userRole = localStorage.getItem('user_role');
      if (this.userRole !== 'member') {
        this.creditBalance = await this.getUsableCredit();
      }
      this.address = this.navCtrl.getParam('address') || this.dialogParams?.address;
      this.allowsInvoiceAutopay = this.navCtrl.getParam('allowsInvoiceAutopay') || this.dialogParams?.allowsInvoiceAutopay || await this.checkIfAllowsInvoiceAutopay();
      this.privatePlanId = this.navCtrl.getParam('privatePlanId') || this.dialogParams?.privatePlanId || this.getPrivatePlanId();
      this.serviceCategoryItems = await this.getServiceCategoryItems();
      await this.getServiceItems();
      this.jobTimeItems = this.bookJob.getJobTimeItems();
      const today = LuxonDateTime.local().startOf('day').toFormat('yyyy-MM-dd');
      this.form.patchValue({
        serviceCategory: this.servicesResponse[0].serviceType.key,
        service: this.serviceItems[0].value,
        time: this.jobTimeItems[0].value,
        date: today
      })
      this.cameFromGetStarted = this.navCtrl.getParam('cameFromGetStarted') || this.dialogParams?.cameFromGetStarted;
      this.hasCreditCard = await this.card.hasValidCard();
      if (this.hasCreditCard) {
        const cards = await this.card.getCreditCards();
        this.lastFourCC = this.getLastFourFromCards(cards);
        this.howToPayItems = this.buildHowToPayItems(cards);
      }
      this.setupPriceListener();
      this.loaded = true;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  ngOnDestroy() {
    if (this.priceSubscription) {
      this.priceSubscription.unsubscribe();
    }
  }

  setupPriceListener() {
    const priceControl = this.form.get('price');
    if (priceControl) {
      // No skip() here: valueChanges never emits on subscribe, so skipping the
      // first emission swallows a pasted amount entirely (a paste is a single
      // emission carrying the final value) and leaves the summary loading forever.
      this.priceSubscription = priceControl.valueChanges.pipe(
        distinctUntilChanged(),
        debounceTime(500)
      ).subscribe(async () => {
        await this.getPaymentPreview();
      });
    }
  }

  async getUsableCredit() {
    const billingData = await this.client.getBillingData();
    let credit = 0;
    billingData.balance_details.map((detail) => {
      const cameFromEmployer = detail.transferred_from !== null;
      if (cameFromEmployer && detail.amount_left > 0) {
        credit += detail.amount_left;
      }
    });
    return credit;
  }

  async checkIfAllowsInvoiceAutopay() {
    const autopaySettings = await this.pros.getAutopaySettings(this.pro.team.id);
    let allowsInvoiceAutopay = false;
    autopaySettings.map((settings) => {
      if (settings?.key === 'payment.autopay') {
        allowsInvoiceAutopay = settings?.value === "true";
      }
    });
    return allowsInvoiceAutopay;
  }

  getPrivatePlanId() {
    let id = null;
    if (this.address.plans.length) {
      if (this.pro.is_private) {
        this.address.plans.map((plan) => {
          if (plan.is_private) {
            id = plan.id;
          }
        });
      }
    }
    return id;
  }

  async getServiceCategoryItems() {
    const categories = await this.pros.getServiceCategories();
    return categories.map((category) => ({
        value: category.key,
        viewValue: category.name
      }));
  }

  async getServiceItems() {
    this.servicesResponse = await this.bookJob.fetchTeamServices(this.pro.homekeeper.id);
    this.parseServiceItemsByCategory(this.servicesResponse[0].serviceType.key);
  }

  parseServiceItemsByCategory(category) {
    this.serviceItems = [];
    this.servicesResponse.map((service) => {
      const alreadyAdded = this.serviceItems.find(item => service.title === item.viewValue);
      if (!alreadyAdded && service.serviceType.key === category) {
        this.serviceItems.push({
          viewValue: service.title,
          value: service.key
        });
      }
    });
    // Auto-select the first service item when category changes
    if (this.serviceItems.length > 0) {
      this.form.patchValue({
        service: this.serviceItems[0].value
      });
    }
  }

  getLastFourFromCards(cards: any[]): string {
    let lastFourCC = null;
    if (cards && cards.length > 0) {
      cards.map((card: any) => {
        if (card.is_primary) {
          lastFourCC = card.metadata?.last4;
        }
      });
    }
    return lastFourCC;
  }

  buildHowToPayItems(cards: any[]) {
    const options = [];

    if (!cards || cards.length === 0) {
      return options;
    }

    // If only one card exists, don't show selection
    if (cards.length === 1) {
      // User will use the default card, no need to show options
      return options;
    }

    // Add primary card option if it exists
    if (this.lastFourCC) {
      options.push({
        viewValue: `Use my primary credit card on file (ends in ${this.lastFourCC})`,
        value: 'currentCard'
      });
    }

    // Add all other cards (including non-primary cards)
    cards.forEach((card: any) => {
      if (!card.is_primary && card.metadata?.last4) {
        options.push({
          viewValue: `Use card ending in ${card.metadata.last4}`,
          value: card.id
        });
      }
    });

    return options;
  }

  @Loading('', true)
  async sendPayment() {
    try {
      this.submitted = true;
      if (!this.form.valid) {
        return;
      }
      const payload = this.getSendPaymentPayload();
      await this.pros.sendPayment(payload, this.pro.homekeeper.id);
      if (this.userRole !== 'member') {
        this.creditBalance = await this.getUsableCredit();
      }
      this.showInvoicePaidSuccess();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  getSendPaymentPayload() {
    const priceValue = this.form.value.price;
    // Check if value contains decimal point (masked value like "$12.00" or "12.00")
    if (priceValue.includes('.')) {
      // Value is masked with decimal, remove mask to get cents
      this.paymentAmount = parseInt(this.util.removeCurrencyMask(priceValue), 10);
    } else {
      // Value is not fully masked (just a number like "12"), convert dollars to cents
      const cleanValue = this.util.removeCurrencyMask(priceValue);
      this.paymentAmount = parseInt(cleanValue, 10) * 100;
    }
    // Convert date to yyyy-MM-dd format
    let formattedDate = this.form.value.date;
    if (formattedDate) {
      // Check if it's a Luxon DateTime object
      if (formattedDate instanceof LuxonDateTime || (formattedDate.ts && formattedDate._zone)) {
        formattedDate = formattedDate.toFormat('yyyy-MM-dd');
      } else if (typeof formattedDate === 'string' && (formattedDate.includes('T') || formattedDate.includes('Z'))) {
        // Date is in ISO string format, convert to yyyy-MM-dd
        formattedDate = LuxonDateTime.fromISO(formattedDate).toFormat('yyyy-MM-dd');
      }
      // If already in yyyy-MM-dd format (string), leave as is
    }
    const payload: any = {
      homekeeper_id: this.pro.homekeeper.id,
      amount: this.paymentAmount,
      address_id: this.address.id,
      job: {
        date: formattedDate,
        start_time: this.form.value.time,
        service_type_key: this.form.value.service
      }
    }
    // Use available employer credits before charging the card
    const creditsFullyCover = this.creditBalance >= this.paymentAmount;
    if (this.creditBalance > 0) {
      payload.credits_to_use = [{
        from_type: 'any',
        amount_to_use: Math.min(this.creditBalance, this.paymentAmount)
      }];
    }
    // Only include payment_method when credits don't fully cover the amount
    if (!creditsFullyCover) {
      if (this.form.value.howToPay === 'currentCard') {
        payload.payment_method = {
          type: 'default_payment_method'
        };
      } else {
        payload.payment_method = {
          type: 'payment_method',
          id: this.form.value.howToPay
        };
      }
    }
    return payload;
  }


  showInvoicePaidSuccess() {
    const params = {
      proId: this.pro.homekeeper.id
    }
    this.util.showSuccess('Payment Sent');
    this.rightSidePanelService.navigateTo(`my-pro/${this.pro.homekeeper.id}`, params, MyProPage);
  }

  goToAddCard() {
    const params = {
      cameFromSendPayment: true,
      pro: this.pro,
      creditBalance: this.creditBalance,
      address: this.address,
      allowsInvoiceAutopay: this.allowsInvoiceAutopay
    }
    this.rightSidePanelService.navigateTo('get-started-card-on-file/card', params);
  }

  async getPaymentPreview() {
    this.paymentPreview = null;
    this.totalOwed = 0;
    if (this.form.value.price === '') {
      return;
    }
    try {
      const payload = this.getSendPaymentPayload();
      this.paymentPreview = await this.pros.getSendPaymentPreview(this.pro.homekeeper.id, payload);
      this.totalOwed = this.paymentPreview.total_fee_amount + this.paymentAmount;
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message);
    }
  }

}
